import React, { useState, useCallback, useRef } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  listSuppressEmails,
  addSuppressEmail,
  deleteSuppressEmail,
} from "../redux/actions/supressEmailActions";
import useCallOnce from "../hooks/useCallOnce";
import useConfirmation from "../hooks/useConfirmation";
import ConfirmationDialog from "../Components/reuse/ConfirmationDialog";

function Emails() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    list: suppressEmailList,
    loading: suppressEmailLoading,
    adding,
    deleting,
    pagination: suppressEmailPagination,
  } = useSelector((state) => state.supressEmail);

  const [filters, setFilters] = useState({
    email: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Modal state
  const [addEmailModalOpen, setAddEmailModalOpen] = useState(false);
  const [newEmailValue, setNewEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [deletingEmailId, setDeletingEmailId] = useState(null);

  // Confirmation dialog
  const { confirmationState, showConfirmation, handleConfirm, handleClose } = useConfirmation();

  // Track initial load to prevent multiple calls
  const initialLoadCalled = useRef(false);

  // Load suppress emails on mount
  useCallOnce("emails/listSuppressEmails", () => {
    if (!initialLoadCalled.current && !suppressEmailLoading) {
      initialLoadCalled.current = true;
      dispatch(listSuppressEmails(page + 1, rowsPerPage, filters.email || ""));
    }
  }, [dispatch]);

  // Note: Filtering will be implemented server-side in the future
  // For now, we show all items from the current page

  // Email validation function
  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return "Email is required";
    }
    
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(trimmedEmail)) {
      return "Please enter a valid email address";
    }
    
    // Check for basic email format issues
    if (trimmedEmail.length > 254) {
      return "Email address is too long";
    }
    
    // Check if email already exists in the list
    const emailExists = suppressEmailList.some(
      (item) => item.email?.toLowerCase() === trimmedEmail.toLowerCase()
    );
    
    if (emailExists) {
      return "This email is already suppressed";
    }
    
    return "";
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setNewEmailValue(value);
    
    // Clear error when user starts typing
    if (emailError) {
      setEmailError("");
    }
  };

  const handleAddSuppressEmail = async () => {
    const trimmedEmail = newEmailValue.trim();
    
    // Validate email
    const validationError = validateEmail(trimmedEmail);
    if (validationError) {
      setEmailError(validationError);
      return;
    }
    
    // Clear any previous errors
    setEmailError("");
    
    try {
      await dispatch(addSuppressEmail({ email: trimmedEmail }));
      setNewEmailValue("");
      setEmailError("");
      setAddEmailModalOpen(false);
      // Refresh the list with current pagination after adding
      dispatch(listSuppressEmails(page + 1, rowsPerPage));
    } catch (error) {
      // Error is already handled by the action
    }
  };

  const handleDeleteSuppressEmail = async (id) => {
    const confirmed = await showConfirmation({
      title: "Unsuppress Email",
      message: "Are you sure you want to unsuppress this email?",
      confirmText: "Unsuppress",
      cancelText: "Cancel",
      confirmColor: "error",
    });

    if (confirmed) {
      setDeletingEmailId(id);
      try {
        await dispatch(deleteSuppressEmail(id));
        // Refresh the list with current pagination after deleting
        dispatch(listSuppressEmails(page + 1, rowsPerPage, filters.email || ""));
      } catch (error) {
        // Error is already handled by the action
      } finally {
        setDeletingEmailId(null);
      }
    }
  };

  const handleFilter = () => {
    setPage(0);
    dispatch(listSuppressEmails(1, rowsPerPage, filters.email || ""));
  };

  const handleReset = () => {
    const reset = {
      email: "",
    };
    setFilters(reset);
    setPage(0);
    dispatch(listSuppressEmails(1, rowsPerPage));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(listSuppressEmails(newPage + 1, rowsPerPage, filters.email || ""));
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(listSuppressEmails(1, newRowsPerPage, filters.email || ""));
  };

  const handleOpenAddEmailModal = () => {
    setAddEmailModalOpen(true);
    setNewEmailValue("");
    setEmailError("");
  };

  const handleCloseAddEmailModal = () => {
    setAddEmailModalOpen(false);
    setNewEmailValue("");
    setEmailError("");
  };

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Suppress Emails
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Manage suppressed email addresses
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddEmailModal}
            sx={{
              bgcolor: "#111827",
              "&:hover": { bgcolor: "#0b1220" },
              textTransform: "none",
            }}
          >
            Add Email
          </Button>
        </Box>

        {/* Filters Row */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label="Email"
                placeholder="Enter email to search"
                value={filters.email}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, email: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: { xs: "column", md: "row" },
                  "& > *": {
                    flex: { xs: 1, md: "none" },
                  },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleFilter}
                  startIcon={<FilterIcon />}
                  size="medium"
                  sx={{
                    bgcolor: "#111827",
                    "&:hover": { bgcolor: "#0b1220" },
                    textTransform: "none",
                  }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RefreshIcon />}
                  size="medium"
                  sx={{
                    borderColor: "#111827",
                    color: "#111827",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#0b1220",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Suppress Emails List */}
        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 70,
                  }}
                >
                  S. No
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                  }}
                >
                  Email Address
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 100,
                    width: 100,
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppressEmailLoading ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "center", py: 8, height: 300 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ color: "#6b7280" }}>
                        Loading suppressed emails...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : suppressEmailList.length > 0 ? (
                suppressEmailList.map((item, index) => (
                  <TableRow key={item._id || item.id || index} hover>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#374151", fontWeight: 500 }}>
                        {item.email || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1, textAlign: "center" }}>
                      <IconButton
                        onClick={() => handleDeleteSuppressEmail(item._id || item.id)}
                        disabled={deletingEmailId === (item._id || item.id)}
                        size="small"
                        sx={{ color: "#ef4444", "&:hover": { backgroundColor: "#fee2e2" } }}
                      >
                        {deletingEmailId === (item._id || item.id) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "center", py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          backgroundColor: "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: "#d1d5db",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: "#9ca3af",
                              borderRadius: "50%",
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ color: "#374151", mb: 1 }}>
                        No suppressed emails found
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                        Suppressed emails will appear here when added
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {suppressEmailList.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={suppressEmailPagination.totalCount || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sx={{
              borderTop: "1px solid #e5e7eb",
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.875rem",
                color: "#6b7280",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.875rem",
              },
            }}
          />
        )}

        {/* Add Email Modal */}
        <Dialog
          open={addEmailModalOpen}
          onClose={handleCloseAddEmailModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Suppress Email
            </Typography>
            <IconButton
              onClick={handleCloseAddEmailModal}
              size="small"
              sx={{ color: "#6b7280" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                placeholder="Enter email to suppress (e.g., hello@dl1961.com)"
                value={newEmailValue}
                onChange={handleEmailChange}
                onBlur={() => {
                  // Validate on blur
                  if (newEmailValue.trim()) {
                    const validationError = validateEmail(newEmailValue.trim());
                    setEmailError(validationError);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !emailError && newEmailValue.trim()) {
                    handleAddSuppressEmail();
                  }
                }}
                error={!!emailError}
                helperText={emailError}
                size="medium"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseAddEmailModal}
              variant="outlined"
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSuppressEmail}
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!newEmailValue.trim() || adding || !!emailError}
              sx={{
                bgcolor: "#111827",
                "&:hover": { bgcolor: "#0b1220" },
                textTransform: "none",
              }}
            >
              {adding ? <CircularProgress size={20} color="inherit" /> : "Add Email"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={confirmationState.open}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title={confirmationState.title}
          message={confirmationState.message}
          confirmText={confirmationState.confirmText}
          cancelText={confirmationState.cancelText}
          confirmColor={confirmationState.confirmColor}
          loading={confirmationState.loading}
        />
      </Box>
    </Layout>
  );
}

export default Emails;
